/**
 * Journal domain hooks for PowerSync (SolidJS).
 *
 * Each hook wraps:
 *   - The canonical `queryKey` from the centralized factory
 *   - The `queryFn` (Drizzle query builder)
 *   - The call to `createPowerSyncQuery` / `createPowerSyncSingle`
 *
 * Consumers import these hooks instead of calling
 * `createPowerSyncQuery` manually, so subscriptions cannot be duplicated or
 * mismatched key↔query.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type {
  ActivityRecent,
  Attachment,
  EntryVersion,
  JournalEntry,
  JournalTag,
  PowerSyncDrizzleDB,
} from "@journal/powersync";

import {
  type EntryFilters,
  filterEntriesQuery,
  getActivityFeedQuery,
  getAllEntryTagsQuery,
  getDeletedEntriesQuery,
  getEntityActivityQuery,
  getEntriesByDateQuery,
  getEntriesByTagQuery,
  getEntryByIdQuery,
  getEntryDaysQuery,
  getEntryTagsQuery,
  getEntryVersionsQuery,
  getFavoriteEntriesQuery,
  getJournalTotalsQuery,
  getMonthlyMoodQuery,
  getMonthlyStatsQuery,
  getMoodDistributionQuery,
  getMoodEntriesQuery,
  getOnThisDayQuery,
  getPhotosByEntryQuery,
  getTagsQuery,
  getUserPhotosQuery,
  queryKeys,
  searchEntriesQuery,
} from "../index";

import { createPowerSyncQuery, createPowerSyncSingle } from "./create-power-sync-query";

// ─── Shared types ────────────────────────────────────────────────────────────

interface BaseJournalQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

export interface EntryDayRow {
  entry_date: string | null;
  entries: number;
}

export interface EntryTagRow {
  id: string;
  name: string | null;
  color: string | null;
  link_id: string;
}

export interface AllEntryTagsRow {
  entry_id: string | null;
  tag_id: string;
  name: string | null;
  color: string | null;
}

export interface JournalTotalsRow {
  entries: number;
  days: number;
  words: number;
}

export interface MonthlyStatsRow {
  month: string;
  days: number;
  entries: number;
  words: number;
}

function enabledWithUser(options: BaseJournalQueryOptions): () => boolean {
  return () => Boolean(options.userId()) && Boolean(options.drizzle());
}

// ─── Entries ──────────────────────────────────────────────────────────────────

/** Entries for a day (Today screen / calendar day). */
export function useEntriesByDateQuery(
  options: BaseJournalQueryOptions & { dateKey: Accessor<string> },
) {
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.byDate(options.userId(), options.dateKey()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getEntriesByDateQuery(db, uid, options.dateKey()) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** One entry by id (detail/edit). */
export function useEntryByIdQuery(
  options: Omit<BaseJournalQueryOptions, "userId"> & { entryId: Accessor<string | undefined> },
) {
  return createPowerSyncSingle<JournalEntry>({
    queryKey: () => queryKeys.journal.detail(options.entryId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.entryId();
      return db && id ? getEntryByIdQuery(db, id) : null;
    },
    enabled: () => Boolean(options.entryId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Days with entries (streaks + calendar points). */
export function useEntryDaysQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<EntryDayRow>({
    queryKey: () => queryKeys.journal.days(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getEntryDaysQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Instant local full-text search. */
export function useSearchEntriesQuery(
  options: BaseJournalQueryOptions & { term: Accessor<string> },
) {
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.search(options.userId(), options.term()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const term = options.term().trim();
      return db && uid && term.length > 0 ? searchEntriesQuery(db, uid, term) : null;
    },
    enabled: () => enabledWithUser(options)() && options.term().trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/**
 * Combined filter (F2): text + mood + tag + range. Reactive to the
 * filters; the `queryKey` carries a stable signature of all of them.
 */
export function useFilterEntriesQuery(
  options: BaseJournalQueryOptions & { filters: Accessor<EntryFilters> },
) {
  const signature = () => {
    const f = options.filters();
    return JSON.stringify([
      f.term?.trim().toLowerCase() ?? "",
      f.mood ?? null,
      f.tagId ?? null,
      f.startKey ?? null,
      f.endKey ?? null,
    ]);
  };
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.filter(options.userId(), signature()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? filterEntriesQuery(db, uid, options.filters()) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** "On this day" — same MM-DD in previous years. */
export function useOnThisDayQuery(
  options: BaseJournalQueryOptions & { todayKey: Accessor<string> },
) {
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.onThisDay(options.userId(), options.todayKey()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getOnThisDayQuery(db, uid, options.todayKey()) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Favorite entries. */
export function useFavoriteEntriesQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.favorites(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getFavoriteEntriesQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

// ─── Tags ────────────────────────────────────────────────────────────────────

/** User tags. */
export function useTagsQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<JournalTag>({
    queryKey: () => queryKeys.journal.tags(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getTagsQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Tags for an entry. */
export function useEntryTagsQuery(
  options: Omit<BaseJournalQueryOptions, "userId"> & { entryId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<EntryTagRow>({
    queryKey: () => queryKeys.journal.entryTags(options.entryId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.entryId();
      return db && id ? getEntryTagsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.entryId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Map of entry→tags for the whole user (entry lists). */
export function useAllEntryTagsQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<AllEntryTagsRow>({
    queryKey: () => queryKeys.journal.allEntryTags(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getAllEntryTagsQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Entries with a specific tag. */
export function useEntriesByTagQuery(
  options: BaseJournalQueryOptions & { tagId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.byTag(options.userId(), options.tagId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const tagId = options.tagId();
      return db && uid && tagId ? getEntriesByTagQuery(db, uid, tagId) : null;
    },
    enabled: () => enabledWithUser(options)() && Boolean(options.tagId()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

// ─── Photos ──────────────────────────────────────────────────────────────────

/** Photos for an entry. */
export function usePhotosByEntryQuery(
  options: Omit<BaseJournalQueryOptions, "userId"> & { entryId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<Attachment>({
    queryKey: () => queryKeys.journal.photos(options.entryId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.entryId();
      return db && id ? getPhotosByEntryQuery(db, id) : null;
    },
    enabled: () => Boolean(options.entryId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Photos from all of the user's entries. */
export function useUserPhotosQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<Attachment>({
    queryKey: () => queryKeys.journal.allPhotos(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getUserPhotosQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

// ─── Statistics ──────────────────────────────────────────────────────────────

/** Global totals (entries, days, words). */
export function useJournalTotalsQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncSingle<JournalTotalsRow>({
    queryKey: () => queryKeys.journal.totals(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getJournalTotalsQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Smooth monthly statistics. */
export function useMonthlyStatsQuery(options: BaseJournalQueryOptions & { limit: number }) {
  return createPowerSyncQuery<MonthlyStatsRow>({
    queryKey: () => queryKeys.journal.monthly(options.userId(), options.limit),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMonthlyStatsQuery(db, uid, options.limit) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

// ─── Mood analytics ──────────────────────────────────────────────────────────

export interface MoodDistributionRow {
  mood: number | null;
  count: number;
}

export interface MonthlyMoodRow {
  bucket: string;
  avg: number;
  count: number;
}

export interface MoodEntryRow {
  entry_date: string | null;
  mood: number | null;
}

/** Mood distribution (count by value 1–5). */
export function useMoodDistributionQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<MoodDistributionRow>({
    queryKey: () => queryKeys.journal.moodDistribution(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMoodDistributionQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Mood time series by month (average + count). */
export function useMonthlyMoodQuery(options: BaseJournalQueryOptions & { limit: number }) {
  return createPowerSyncQuery<MonthlyMoodRow>({
    queryKey: () => queryKeys.journal.monthlyMood(options.userId(), options.limit),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMonthlyMoodQuery(db, uid, options.limit) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Raw mood rows (input for client-side weekly aggregation). */
export function useMoodEntriesQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<MoodEntryRow>({
    queryKey: () => queryKeys.journal.moodEntries(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getMoodEntriesQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

// ─── Version history (L4) and trash (L2) ─────────────────────────────────────

/** Local versions of an entry (latest 10 per bucket), newest first. */
export function useEntryVersionsQuery(
  options: Omit<BaseJournalQueryOptions, "userId"> & { entryId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<EntryVersion>({
    queryKey: () => queryKeys.journal.versions(options.entryId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.entryId();
      return db && id ? getEntryVersionsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.entryId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Entries in the trash, most recently deleted first. */
export function useDeletedEntriesQuery(options: BaseJournalQueryOptions) {
  return createPowerSyncQuery<JournalEntry>({
    queryKey: () => queryKeys.journal.trash(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getDeletedEntriesQuery(db, uid) : null;
    },
    enabled: enabledWithUser(options),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

// ─── Activity (L3) ──────────────────────────────────────────────────────────

/** Local activity feed (bucket `activity_recent`, ~90 days). */
export function useActivityFeedQuery(options: BaseJournalQueryOptions & { limit?: number }) {
  return createPowerSyncQuery<ActivityRecent>({
    queryKey: () => queryKeys.journal.activity(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getActivityFeedQuery(db, { limit: options.limit }) : null;
    },
    enabled: enabledWithUser(options),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}

/** Activity history for a specific entity (sheet "Historial"). */
export function useEntityActivityQuery(
  options: BaseJournalQueryOptions & {
    entityType: Accessor<string>;
    entityId: Accessor<string | undefined>;
    limit?: number;
  },
) {
  return createPowerSyncQuery<ActivityRecent>({
    queryKey: () =>
      queryKeys.journal.activityEntity(
        options.userId(),
        options.entityType(),
        options.entityId() ?? "",
      ),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.entityId();
      return db && id
        ? getEntityActivityQuery(db, options.entityType(), id, { limit: options.limit })
        : null;
    },
    enabled: () => Boolean(options.entityId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime,
  });
}
